import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getTitleById } from "@/lib/catalog";

const AUTOPLAY_MS = 6000;

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => setIndex((next + heroSlides.length) % heroSlides.length);
  const slide = heroSlides[index];
  const film = getTitleById(slide.titleId);

  return (
    <section className="relative w-full overflow-hidden bg-background-950">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {heroSlides.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={item.backdrop}
              alt={`${getTitleById(item.titleId)?.title ?? "影视"} 焦点图`}
              title={`${getTitleById(item.titleId)?.title ?? ""} - 欧美影视推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/30" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-4 pb-10 md:px-8 md:pb-16">
          {film ? (
            <div key={slide.id} className="max-w-2xl carousel-fade-in">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-foreground-950">
                  {film.type === "movie" ? "电影" : "电视剧"}
                </span>
                {film.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md border border-foreground-100/30 px-2 py-0.5 text-[11px] text-foreground-100"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="mt-4 font-heading text-3xl font-bold text-foreground-50 md:text-5xl">
                {film.title}
              </h2>
              <p className="mt-1 font-label text-sm tracking-widest text-accent-400">
                {film.originalTitle}
              </p>
              <p className="mt-3 text-sm text-foreground-200 md:text-base">{slide.tagline}</p>
              <p className="mt-2 text-xs text-foreground-400 md:text-sm">
                {film.year} · {film.genres.join(" / ")} · {film.region} · 评分 {film.rating}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${film.id}`}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-fill text-lg" />
                  查看详情
                </Link>
                <Link
                  to={`/category?type=${film.type}`}
                  className="inline-flex items-center gap-2 rounded-md border border-foreground-100/30 px-5 py-2.5 text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400 whitespace-nowrap cursor-pointer"
                >
                  更多{film.type === "movie" ? "电影" : "剧集"}
                </Link>
              </div>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/60 text-foreground-100 transition-colors hover:bg-primary-500 md:left-6 md:h-11 md:w-11 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/60 text-foreground-100 transition-colors hover:bg-primary-500 md:right-6 md:h-11 md:w-11 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 md:bottom-6">
          {heroSlides.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-foreground-100/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}